import {
  EsrsAssessmentMaterialStandardsQuery_,
  EsrsAssessmentsListDocument_,
  UpsertEsrsAssessmentMutation_,
  useEsrsAssessmentProjectLeaderQuery,
  useEsrsAssessmentsListQuery,
} from 'models';
import { useCompanyType, useToast } from 'utils/hooks';
import { useMemo } from 'react';
import { EsrsAssessmentDuplicationRequest } from '../../../../../backend/functions/esrs/duplicate-esrs-assessment';
import { useApolloClient } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { DREnums } from '..';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';

export const useGetEsrsAssessmentList = () => {
  const { companyId = '' } = useParams();
  const { companyType, loading: companyTypeLoading } = useCompanyType();
  const isGroup = useMemo(() => companyType === 'group-company', [companyType]);

  const { data, loading } = useEsrsAssessmentsListQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const esrsAssessments = useMemo(() => data?.esrsAssessments ?? [], [data]);

  return {
    esrsAssessments,
    isGroup,
    loading: loading || companyTypeLoading,
  };
};

export const useEsrsAssessmentProjectLeader = (esrsAssessmentId: string) => {
  const { data } = useEsrsAssessmentProjectLeaderQuery({
    variables: {
      esrsAssessmentId,
    },
    skip: !esrsAssessmentId,
  });

  const esrsAssessmentProjectLeaderId = useMemo(() => {
    return data?.esrsAssessment?.projectLeaderId;
  }, [data]);

  return { esrsAssessmentProjectLeaderId };
};

export type GroupMetricMaterialityResult = {
  groupOnly: string[];
  subsidiariesOnly: string[];
};

export type CompanyMetricMaterialityResult = {
  companyOnly: string[];
  parentCompanyOnly: string[];
};

export type EsrsAssessmentDuplicationOptions =
  EsrsAssessmentDuplicationRequest['duplicationOptions'];

export const defaultEsrsDuplicationOptions: EsrsAssessmentDuplicationOptions = {
  withMateriality: true,
  withComments: true,
  withDatapoints: true,
  withDocumentation: true,
  withReportingUnits: true,
  withTargetsAndActions: true,
  onlyNarrativeAnswers: false,
};

export const useDuplicateEsrsAssessment = () => {
  const client = useApolloClient();
  const toast = useToast();
  const nhost = useNhostClient();

  const duplicateAssessment = async (
    assessmentId: string,
    newReportingYear: number,
    duplicationOptions: EsrsAssessmentDuplicationOptions
  ) => {
    try {
      toast({ text: 'Duplicating ESRS assessment...' });
      const res = await nhost.functions.post<{
        duplicatedAssessment: UpsertEsrsAssessmentMutation_['insert_EsrsAssessment_one'];
      }>('/esrs/duplicate-esrs-assessment', {
        assessmentId: assessmentId,
        newReportingYear: newReportingYear,
        duplicationOptions: duplicationOptions || defaultEsrsDuplicationOptions,
      });

      await client.refetchQueries({ include: [EsrsAssessmentsListDocument_] });
      if (res.body?.duplicatedAssessment) {
        toast({
          text: `Assessment duplicated successfully for year ${res.body?.duplicatedAssessment?.reportingYear}`,
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        text: 'Failed to duplicate assessment',
        variant: 'danger',
      });
    }
  };

  return duplicateAssessment;
};

export const getRelevantDR = (
  metric: NonNullable<
    EsrsAssessmentMaterialStandardsQuery_['esrsAssessment']
  >['materialStandards'][number]['materialMetrics'][number]['metric'],
  frameworkRef: string
) => {
  if (metric.requirement.frameworkRef === frameworkRef) return metric.requirement.reference;

  const frameworkMatchedDr =
    metric.mfDisclosureRequirements?.[0]?.mfDisclosureRequirement?.drToMFDisclosureRequirements?.find(
      (mfDr) => mfDr.disclosureRequirement.frameworkRef === frameworkRef
    );
  const multiFrameworkDR = metric.mfDisclosureRequirements?.[0]?.mfDisclosureRequirement?.reference;
  const relevantDR = frameworkMatchedDr
    ? frameworkMatchedDr?.disclosureRequirement.reference
    : multiFrameworkDR;

  return relevantDR;
};

export const drProgressConditions = (
  materialMetric: {
    isMaterial?: boolean | null | undefined;
    metric: NonNullable<
      EsrsAssessmentMaterialStandardsQuery_['esrsAssessment']
    >['materialStandards'][number]['materialMetrics'][number]['metric'];
  },
  standardApDrs: string[],
  frameworkRef: string
) => {
  return (
    materialMetric.isMaterial &&
    materialMetric.metric.isAssessable &&
    materialMetric.metric.requirement.type !== DREnums.action &&
    materialMetric.metric.requirement.type !== DREnums.target &&
    standardApDrs.includes(getRelevantDR(materialMetric.metric, frameworkRef))
  );
};
